"use client";

import { createContext, use, useCallback, useMemo } from "react";

export const BASE_PANE_KEY = "__base__";

export type DialogPaneDirection = "initial" | "forward" | "backward";

export interface DialogStackPane {
  key: string;
  present: boolean;
}

export interface DialogStackInterface {
  /** Panes in push order, base pane first. */
  panes: DialogStackPane[];
  /** The pane the user is looking at. */
  activeKey: string;
  direction: DialogPaneDirection;
  /** Element every pushed pane is portalled into. `null` until the stack has mounted. */
  viewport: HTMLDivElement | null;
  /** Mount-time registration. Returns the unregister callback. */
  registerPane: (key: string, requestClose: () => void) => () => void;
  setPanePresent: (key: string, present: boolean) => void;
  setPaneHeight: (key: string, height: number) => void;
  setPaneWidth: (key: string, width: string | undefined) => void;
  /** Closes the topmost pushed pane. No-op on the base pane. */
  back: () => void;
}

export const DialogStackContext = createContext<DialogStackInterface | null>(null);

export const useDialogStackContext = () => use(DialogStackContext);

export interface DialogNestedInterface {
  paneKey: string;
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const DialogNestedContext = createContext<DialogNestedInterface | null>(null);

export const useDialogNestedContext = () => use(DialogNestedContext);

export interface UseDialogStackResult {
  /** 0 on the base view, 1 on the first pushed view, and so on. */
  depth: number;
  canGoBack: boolean;
  /** Closes the topmost pushed view, sliding the previous one back in. */
  back: () => void;
}

export const useDialogStack = (): UseDialogStackResult => {
  const stack = useDialogStackContext();
  const depth = stack ? stack.panes.findIndex(pane => pane.key === stack.activeKey) : 0;
  const stackBack = stack?.back;
  const back = useCallback(() => stackBack?.(), [stackBack]);

  return useMemo(
    () => ({
      depth: depth < 0 ? 0 : depth,
      canGoBack: depth > 0,
      back,
    }),
    [depth, back]
  );
};
